import { z } from "zod";

import {
  currencyRegex,
  isoDateRegex,
} from "@/components/Forms/utils/zod/regexHelpers";
import type {
  LabAsset,
  LabAssetType,
  UpdateLabAssetBody,
} from "../labAssets.types";
import { getLabAssetDisplayName } from "../labAssets.utils";

// Pure form types and mappers for the shared asset form. Kept free of React,
// mantine and mfui imports so the request-shaping logic is unit-testable
// (labAssetForm.utils.test.ts) without a component render.

/**
 * Raw form values — the Cost TextInput holds cost as a string. The shared
 * field set only (ENG-1381 pass 1); type-specific fields land in the ENG-1381
 * follow-up PR and asset_type is supplied by the create flow, not this form.
 */
export interface LabAssetFormData {
  // Blank on create lets the API generate one (ENG-928); on edit a blank
  // would clear it, so the form requires it when the asset already has one.
  asset_number?: string | null;
  name?: string | null;
  category?: string | null;
  model?: string | null;
  vendor?: string | null;
  serial_number?: string | null;
  barcode?: string | null;
  purchase_date?: string | null;
  cost?: string | null;
  location?: string | null;
  description?: string | null;
  // A users.user_id; null is "unassigned". The form stores the SelectBox's
  // clear (undefined) as null, so the model stays number | null throughout.
  issued_to_id?: number | null;
}

/**
 * Normalized submit payload: "" → null, cost → number. POST /assets has a
 * .strict() schema (unknown keys 400), so the payload is built key-by-key
 * from this shape — never spread raw form values into a request body.
 */
export type LabAssetPayload = Pick<
  LabAsset,
  | "asset_number"
  | "name"
  | "category"
  | "model"
  | "vendor"
  | "serial_number"
  | "barcode"
  | "purchase_date"
  | "cost"
  | "location"
  | "description"
  | "issued_to_id"
>;

/**
 * Which form fields differ from `DefaultFormData` at submit time (mantine
 * `form.getDirty()`). The edit form uses it to send only what changed.
 */
export type LabAssetDirtyFields = Partial<
  Record<keyof LabAssetFormData, boolean>
>;

// Every key seeded so mantine hands each input a defined value; a missing
// key would mount the input uncontrolled and could never be cleared.
// purchase_date is null (not "") because DateInput commits null on clear.
export const EMPTY_FORM_DATA: Required<LabAssetFormData> = {
  asset_number: "",
  name: "",
  category: "",
  model: "",
  vendor: "",
  serial_number: "",
  barcode: "",
  purchase_date: null,
  cost: "",
  location: "",
  description: "",
  issued_to_id: null,
};

/**
 * Every field the form edits, derived from EMPTY_FORM_DATA (which is
 * `Required<LabAssetFormData>`, so a field added to the form type without a
 * seed is a compile error here — not a key that silently never reaches the
 * PATCH body). toUpdateBody iterates this to build its diff.
 */
export const LAB_ASSET_FORM_FIELDS = Object.keys(
  EMPTY_FORM_DATA
) as readonly (keyof LabAssetFormData)[];

// The assignment fails to compile if the form gains a field that
// LabAssetPayload / UpdateLabAssetBody don't carry — the check the Wave 3
// field tickets (ENG-1674/1675/1677) and the software-field pass need.
type EditableField = keyof LabAssetPayload;
const EDITABLE_FIELDS: readonly EditableField[] = LAB_ASSET_FORM_FIELDS;

/**
 * LabAsset → form values, in each input's committed shape so mantine's
 * initialValues compare clean on mount: text `null` → "", `cost` (a number,
 * or a string from the MySQL driver) → string, `purchase_date` as the stored
 * `YYYY-MM-DD` (DateInput commits null on clear, so null stays null).
 *
 * Name uses the same precedence as the header and table
 * (getLabAssetDisplayName): software rows keep it in `license_name`,
 * equipment in `name`, each falling back to the other for legacy rows.
 */
export const toLabAssetFormData = (asset: LabAsset): LabAssetFormData => ({
  asset_number: asset.asset_number ?? "",
  name: getLabAssetDisplayName(asset),
  category: asset.category ?? "",
  model: asset.model ?? "",
  vendor: asset.vendor ?? "",
  serial_number: asset.serial_number ?? "",
  barcode: asset.barcode ?? "",
  purchase_date: asset.purchase_date ?? null,
  cost:
    asset.cost === null || asset.cost === undefined ? "" : String(asset.cost),
  location: asset.location ?? "",
  description: asset.description ?? "",
  issued_to_id: asset.issued_to_id ?? null,
});

/**
 * Issued To form value → request value. A missing value must reach the API as
 * an explicit null: the PATCH schema's `.optional()` would drop an absent key
 * and read it as "unchanged", so a clear would never unassign.
 */
export const toIssuedToId = (value: number | null | undefined): number | null =>
  value ?? null;

/**
 * Builds the PATCH body from the dirty fields only. The schema is .strict()
 * and merges what it receives, so this is assembled key-by-key (never a
 * spread of form values) and never carries `asset_type`. For software a
 * renamed asset sends `license_name` plus `name: null` — the inverse of the
 * mapping in toLabAssetFormData and the same shape toCreateBody writes. The
 * explicit null matters for legacy rows that still hold their name in
 * `name`: without it the old value would stay behind, and the API's search
 * matches both columns, so the stale name would keep finding the asset.
 */
export const toUpdateBody = (
  payload: LabAssetPayload,
  dirtyFields: LabAssetDirtyFields,
  assetType: LabAssetType
): UpdateLabAssetBody => {
  const body: UpdateLabAssetBody = {};
  const pick = <K extends EditableField>(key: K) => {
    body[key] = payload[key];
  };

  for (const key of EDITABLE_FIELDS) {
    if (!dirtyFields[key]) continue;
    if (key === "name" && assetType === "software") {
      body.license_name = payload.name;
      body.name = null;
      continue;
    }
    pick(key);
  }

  return body;
};

const ASSET_NUMBER_REQUIRED = "Asset Number is required";

// Mirrors the API's trim + max(255) on asset_number (ENG-928).
const assetNumberSchema = z
  .string({ invalid_type_error: ASSET_NUMBER_REQUIRED })
  .max(255, "Asset Number must be 255 characters or fewer");

// Validated against the *normalized* values ("" → null, trimmed), so a
// pasted trailing space never fails a rule the payload would have satisfied.
export const labAssetValidationSchema = z.object({
  // Blank on create lets the API generate the number.
  asset_number: assetNumberSchema.nullable(),
  name: z
    .string({ invalid_type_error: "Name is required" })
    .min(1, "Name is required"),
  category: z.string().nullable(),
  model: z.string().nullable(),
  vendor: z.string().nullable(),
  serial_number: z.string().nullable(),
  // No charset rule: the API, DB column, and barcode search accept any
  // string, and common asset-tag symbologies (Code 39/128) are alphanumeric.
  barcode: z.string().nullable(),
  purchase_date: z
    .string()
    .regex(
      isoDateRegex,
      `Invalid date format. Please select a date or enter in "YYYY-MM-DD" format.`
    )
    .nullable(),
  cost: z
    .string()
    .regex(currencyRegex, "Invalid - please enter a cost number")
    .nullable(),
  location: z.string().nullable(),
  description: z.string().nullable(),
  // A users.user_id; null is "unassigned".
  issued_to_id: z
    .number({ invalid_type_error: "Invalid user" })
    .int("Invalid user")
    .positive("Invalid user")
    .nullable(),
});

// Editing an asset that already has a number: a blank PATCH would write NULL
// and the API only generates on create, so the field becomes required (same
// rule as DefaultStorageForm's requireStorageNumber).
export const requiredAssetNumberSchema = labAssetValidationSchema.extend({
  asset_number: assetNumberSchema.min(1, ASSET_NUMBER_REQUIRED),
});
